// EXPERIMENT #171270395

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import $ from 'jquery';
import 'jquery-mask-plugin';
import { postJson } from 'requests/http';
import classNames from 'classnames';

import Button from 'components/button';
import Input from 'components/input';
import Form from 'components/form';
import CompanyLeadShape from '../constants/CompanyLeadShape';
import validationSchema, { uniqueEmailSchema } from '../validation_schema';
import submitForm from '../submit_form';

const maskPhoneNumberAsync = () => setTimeout(() => $('.format-phone').mask('(999) 999-9999'), 0);

const initialValues = companyLead => ({
  company_lead: {
    company_name: companyLead.company_name || '',
    email: companyLead.email || '',
    full_name: companyLead.full_name || '',
    number_employees: '',
    phone: '',
  },
  user: {
    password: '',
    password_confirmation: '',
  },
});

const inputClassNames = (touched, errors, fieldName, classes) =>
  `form-control fs-block ${
    touched[fieldName] && errors[fieldName] ? 'error-border' : ''
  } ${classes || ''}`;

const emailInputClassNames = (touched, errors, status, fieldName, classes) =>
  `form-control fs-block ${
    touched[fieldName] && (status[fieldName] || errors[fieldName]) ? 'error-border' : ''
  } ${classes || ''}`;

const InnerSignupForm = ({
  contractorOnly,
  formExpandable,
  fromGoogleSSO,
  googleSsoEmail,
  errors,
  isSubmitting,
  setStatus,
  status,
  touched,
  values,
}) => {
  useEffect(() => {
    // Expand form
    if (formExpandable && values.company_lead.email) {
      const hiddenForm = document.querySelector('.signup-form-reveal');
      hiddenForm.style.maxHeight = `${999}px`;
    }

    if (window.blockingExistingUserSignUp) {
      const autoValidate = setTimeout(() => {
        uniqueEmailSchema(postJson)
          .validate(values.company_lead.email)
          .then(() => setStatus({ company_lead: { email: null } }))
          .catch(({ message }) => {
            setStatus({ company_lead: { email: message } });
          });
      }, 300);
      return () => clearTimeout(autoValidate);
    }

    return () => {};
  }, [values.company_lead.email, setStatus, formExpandable]);

  const validationError =
    Object.values(errors).some(value => Object.keys(value).length) ||
    Object.values(status.company_lead).some(value => value !== null);

  return (
    <Form action="/invite/company" data-default-action="/invite/company">
      <Input
        inputClassName={inputClassNames(touched, errors, 'company_lead.full_name')}
        name="company_lead.full_name"
        placeholder="Full name"
        type="text"
      />
      <Input
        inputClassName={emailInputClassNames(touched, errors, status, 'company_lead.email')}
        name="company_lead.email"
        placeholder="Work email address"
        type="email"
      />
      <div className={classNames({ 'signup-form-reveal': formExpandable })}>
        <Input
          inputClassName={inputClassNames(touched, errors, 'company_lead.company_name')}
          name="company_lead.company_name"
          placeholder="Company name"
          type="text"
        />
        <div className="col-12 padding-none d-sm-inline-flex">
          <Input
            formGroupClassName="col-12 col-sm-6 form-group padding-left-none padding-right-none padding-right-sm-10px"
            inputClassName={inputClassNames(touched, errors, 'company_lead.number_employees')}
            name="company_lead.number_employees"
            placeholder={contractorOnly ? 'No. of contractors' : 'No. of employees'}
            type="number"
          />
          <Input
            formGroupClassName="col-12 col-sm-6 padding-none"
            inputClassName={inputClassNames(touched, errors, 'company_lead.phone', 'format-phone')}
            name="company_lead.phone"
            placeholder="Company phone"
            type="tel"
          />
        </div>
        {fromGoogleSSO && values.company_lead.email === googleSsoEmail ? (
          <div />
        ) : (
          <div className="col-12 padding-none d-sm-inline-flex">
            <Input
              formGroupClassName="col-12 col-sm-6 form-group padding-left-none padding-right-none padding-right-sm-10px"
              inputClassName={inputClassNames(touched, errors, 'user.password')}
              name="user.password"
              placeholder="Set your password"
              type="password"
            />
            <Input
              formGroupClassName="col-12 col-sm-6 padding-none"
              inputClassName={inputClassNames(touched, errors, 'user.password_confirmation')}
              name="user.password_confirmation"
              placeholder="Re-enter password"
              type="password"
            />
          </div>
        )}
      </div>
      <Button
        className="btn btn-primary margin-top-10px full-width"
        disabled={validationError || isSubmitting}
        label="Get started"
        type="submit"
      />
    </Form>
  );
};

const LeadFormWithPassword = ({
  contractorOnly,
  formExpandable,
  companyLead,
  onSubmit,
  fromGoogleSSO,
}) => {
  const onFormSubmit = (values, formik) => {
    onSubmit(values, formik.setSubmitting);
  };

  maskPhoneNumberAsync();
  return (
    <Formik
      initialValues={initialValues(companyLead)}
      onSubmit={onFormSubmit}
      validationSchema={validationSchema(fromGoogleSSO, companyLead.email)}
    >
      {({ errors, isSubmitting, setStatus, status, touched, values }) => (
        <InnerSignupForm
          contractorOnly={contractorOnly}
          formExpandable={formExpandable}
          fromGoogleSSO={fromGoogleSSO}
          googleSsoEmail={companyLead.email}
          errors={errors}
          isSubmitting={isSubmitting}
          setStatus={setStatus}
          status={status}
          touched={touched}
          values={values}
        />
      )}
    </Formik>
  );
};

InnerSignupForm.propTypes = {
  contractorOnly: PropTypes.bool.isRequired,
  formExpandable: PropTypes.bool.isRequired,
  fromGoogleSSO: PropTypes.bool.isRequired,
  googleSsoEmail: PropTypes.string,
  errors: PropTypes.objectOf(PropTypes.object).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  setStatus: PropTypes.func.isRequired,
  status: PropTypes.oneOfType([PropTypes.object, PropTypes.elementType]),
  touched: PropTypes.shape({
    company_lead: PropTypes.shape({
      company_name: PropTypes.bool,
      email: PropTypes.bool,
      full_name: PropTypes.bool,
      number_employees: PropTypes.bool,
      phone: PropTypes.bool,
    }),
    user: PropTypes.shape({
      password: PropTypes.bool,
      password_confirmation: PropTypes.bool,
    }),
  }).isRequired,
  values: PropTypes.shape({
    company_lead: PropTypes.shape({
      email: PropTypes.string,
    }),
  }).isRequired,
};

InnerSignupForm.defaultProps = {
  googleSsoEmail: '',
  status: {
    company_lead: {},
  },
};

LeadFormWithPassword.defaultProps = {
  onSubmit: submitForm,
  formExpandable: false,
};

LeadFormWithPassword.propTypes = {
  contractorOnly: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func,
  companyLead: CompanyLeadShape.isRequired,
  fromGoogleSSO: PropTypes.bool.isRequired,
  formExpandable: PropTypes.bool,
};

export default LeadFormWithPassword;
